import * as XLSX from 'xlsx';
import { detectCsvDelimiter, CsvDelimiter, normalizeCsvDelimiter } from './csvDelimiter';

export interface ParsedSpreadsheet {
  headers: string[];
  rows: string[][];
  totalRows: number;
  delimiter?: CsvDelimiter;
}

const isCsv = (file: File) => file.name.toLowerCase().endsWith('.csv');

/**
 * Parses an uploaded spreadsheet into the header/row shape the mapping UI works with.
 *
 * For CSV the delimiter is pinned explicitly rather than left to SheetJS, whose guess
 * counts separators across the first 1024 bytes and can be outvoted by the decimal
 * commas in European exports. `forcedDelimiter` carries the user's manual override.
 */
export async function parseSpreadsheetFile(
  file: File,
  forcedDelimiter?: string
): Promise<ParsedSpreadsheet> {
  try {
    let workbook: XLSX.WorkBook;
    let delimiter: CsvDelimiter | undefined;

    if (isCsv(file)) {
      const text = await file.text();
      delimiter = normalizeCsvDelimiter(forcedDelimiter) ?? detectCsvDelimiter(text);
      workbook = XLSX.read(text, { type: 'string', FS: delimiter });
    } else {
      const buffer = await file.arrayBuffer();
      workbook = XLSX.read(buffer, { type: 'array' });
    }

    const sheetName = workbook.SheetNames[0];
    const sheet = workbook.Sheets[sheetName];
    const rawData: string[][] = XLSX.utils.sheet_to_json(sheet, {
      header: 1,
      defval: '',
      raw: false,
    });

    if (!rawData || rawData.length === 0) {
      throw new Error('File appears to be empty');
    }

    const headers = rawData[0].map(String);
    // Cell formatting (e.g. borders drawn over a range) makes Excel emit rows that hold
    // no values. Drop them here so they are never counted or validated as data — this
    // mirrors the server parser, which resolves skipRowIndices against the same shape.
    const rows = rawData
      .slice(1)
      .map(row => row.map(cell => (cell === null || cell === undefined ? '' : String(cell))))
      .filter(row => row.some(cell => cell.trim() !== ''));

    if (rows.length === 0) {
      throw new Error('File contains no data rows. Please add data below the header row.');
    }

    return { headers, rows, totalRows: rows.length, delimiter };
  } catch (err: any) {
    throw new Error(
      err.message || "Failed to parse file. Please ensure it's a valid Excel or CSV file."
    );
  }
}
